import { useSelector } from "react-redux";

import * as Api from "@/api";
import OverflowDrawer from "@/app/shared/OverflowDrawer";
import urls from "@/constants/Urls";
import EditorForm from "@/design/components/EditorForm";
import SaveIndicator from "@/design/components/SaveIndicator";
import InputMedia from "@/design/forms/InputMedia";
import InputText from "@/design/forms/InputText";
import { any, string } from "@/utils/hooks/endorse";
import useSave, { SaveState } from "@/utils/hooks/useSave";

import PropTypes from "prop-types";

import optionReducer from "./optionReducer";

function OptionForm({ ivr, ivrOption, ivrDispatch }) {
  const soundStore = useSelector((state) => state.domain.sounds);
  const pbx = useSelector((state) => state.domain.pbx);

  const { value, dispatch, setSaveState, formState, errors, allErrors, save } = useSave({
    initialValue: ivrOption,
    reducer: optionReducer,
    validator: () => ({
      overflow: any().required({ message: "Overflow is required" }),
      forceCallerId: string().pattern("^[-'a-zA-Z0-9À-ÖØ-öø-ÿ\\ \\,.+_\"–]*$", {
        message: "The Caller ID can only contain alphanumeric characters, spaces, commas, dots, and dashes.",
      }),
    }),
    onSubmit: () => Api.storeIvrOption(ivr, value),
    onSuccess: (response) => {
      if (!value.id) {
        dispatch({ type: "id", value: response.data.ivrOption.id });
        ivrDispatch({ type: "addOption", option: { ...value, id: response.data.ivrOption.id } });
      } else {
        ivrDispatch({ type: "updateOption", option: value });
      }
    },
  });

  const formHandler = (property, value) => {
    switch (property) {
      case "announcement":
        dispatch({ type: property, value, soundStore });
        break;
      default:
        dispatch({ type: property, value });
    }

    setSaveState(SaveState.UNSAVED);
  };

  return (
    <EditorForm
      title={`Option ${ivrOption?.input}`}
      action={<SaveIndicator formState={formState} errors={allErrors} save={save} mini />}
    >
      <InputMedia
        hasEmpty={null}
        id="announcement"
        label="Announcement"
        onChange={formHandler}
        value={value?.announcement?.id}
        selectValue="id"
        selects={soundStore.list}
        getOptionLabel={(option) => option.name}
        getSourceDependency={(option) => option?.token}
        getSourceGeneration={(option) =>
          `${urls.audio}${pbx.context}%2F${option.id}.${option.contentType}?alt=media&token=${option.token}`
        }
        helperText="Optional announcement heard before the overflow."
        error={errors}
      />
      <OverflowDrawer
        label="Overflow"
        value={value?.overflow}
        propertyName="overflow"
        overflowHandler={(value) => formHandler("overflow", value)}
        error={errors}
      />
      <InputText
        id="forceCallerId"
        label="Caller ID"
        onChange={formHandler}
        value={value?.forceCallerId}
        helperText="Force a different Caller ID when this option is selected."
        error={errors}
      />
    </EditorForm>
  );
}

OptionForm.propTypes = {
  ivr: PropTypes.object,
  ivrOption: PropTypes.object,
  ivrDispatch: PropTypes.func,
};

export default OptionForm;
